import React from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  IconButton,
  Switch,
  FormControl,
  FormLabel,
} from "components-lib";
import { FcVideoCall } from "react-icons/fc";

/**
 * PatientHeader Component
 * 
 * Displays patient information and controls at the top of the patient details page
 * Includes patient name, demographics, speech-to-text toggle, and video call button
 */
const PatientHeader = ({
  patient,
  onBack,
  isSpeechToTextEnabled,
  setIsSpeechToTextEnabled,
  onVideoCallClick,
}) => {
  const iconColor = patient?.type === "in_clinic" ? "gray" : "blue";

  // Determine if speech-to-text is enabled for the current doctor (read-only check)
  let speechEnabledForDoctor = false;
  try {
    const profileData = typeof window !== "undefined" && localStorage.getItem("currentDoctorProfile");
    if (profileData) {
      const profile = JSON.parse(profileData);
      speechEnabledForDoctor = profile.speech_to_text === 1;
    }
  } catch (error) {
    console.warn("Error parsing doctor profile settings for speech-to-text:", error);
  }

  const patientId = patient?.patient_id || patient?.patientCode || patient?.id || "";
  const phone = patient?.phone_no || patient?.mobile_no || patient?.phone || patient?.phoneNumber || "-";
  const name = patient?.name || "Unknown";
  const gender = patient?.gender || "";
  const age = patient?.age ? `${patient.age} Years` : "";

  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="12px"
      px={{ base: "16px", md: "24px" }}
      py={{ base: "14px", md: "16px" }}
      width="100%"
      bg="white"
      boxShadow="sm"
      mb={6}
    >
      <Flex alignItems="center" justifyContent="space-between" gap={4}>
        {/* Back button */}
        <Button 
          variant="outline" 
          colorScheme="blue" 
          size="md" 
          onClick={onBack}
          fontWeight="semibold"
          px={6}
        >
          Back
        </Button>

        {/* Centered compact patient summary */}
        <Flex flex="1" justifyContent="center" px={{ base: "8px", md: "16px" }}>
          <Text
            textAlign="center"
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="semibold"
            color="gray.700"
            noOfLines={1}
          >
            {patientId ? `${patientId}  /  ` : ""}
            <span style={{ fontWeight: 700 }}>{name}</span>
            {gender ? `  /  ${gender}` : ""}
            {phone ? `  /  ${phone}` : ""}
            {age ? `  /  ${age}` : ""}
          </Text>
        </Flex>

        {/* Right side: small speech toggle (if available) + video icon */}
        <Flex alignItems="center" gap="12px">
          {speechEnabledForDoctor && (
            <FormControl display="flex" alignItems="center" gap={2}>
              <FormLabel htmlFor="speech-to-text" mb="0" fontSize="sm" fontWeight="medium">
                STT
              </FormLabel>
              <Switch
                id="speech-to-text"
                isChecked={isSpeechToTextEnabled}
                onChange={(e) => setIsSpeechToTextEnabled(e.target.checked)}
                colorScheme="blue"
                size="sm"
              />
            </FormControl>
          )}

          <IconButton
            icon={<FcVideoCall />}
            variant="ghost"
            aria-label="Video Call"
            fontSize={{ base: "20px", md: "26px" }}
            onClick={onVideoCallClick}
            isDisabled={patient?.type === "in_clinic"}
          />
        </Flex>
      </Flex>
    </Box>
  );
};

export default PatientHeader;